import { createSlice } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';

export interface TrackSummary {
  id: string;
  name: string;
  activityType: 'running' | 'cycling' | 'hiking' | 'climbing' | 'other';
  totalDistance: number;
  totalDuration: number;
  totalElevationGain: number;
  totalElevationLoss: number;
  avgSpeed: number;
  startTime: string;
  endTime: string;
  createdAt: string;
  videoUrl?: string;
  renderStatus?: 'queued' | 'rendering' | 'completed' | 'failed';
  renderProgress?: number;
  simplifiedCoordinates?: [number, number, number][];
}

export interface TelemetryPoint {
  lat: number;
  lon: number;
  ele: number;
  time: string;
  hr?: number;
  cadence?: number;
  power?: number;
  speed?: number;
  distance?: number;
}

export interface TrackDetail extends TrackSummary {
  telemetry: {
    points: TelemetryPoint[];
  };
  bounds: {
    minLat: number;
    maxLat: number;
    minLon: number;
    maxLon: number;
  };
}

export interface RenderJobStatus {
  jobId: string;
  status: 'queued' | 'rendering' | 'completed' | 'failed';
  progress: number;
  startedAt?: string;
  elapsed?: number;
  etaSeconds?: number;
  completionTime?: string;
  videoUrl?: string;
  error?: string;
  trackId?: string;
  resolution?: string;
  fps?: number;
  userId?: string;
}

export interface SyncStatus {
  provider: string;
  status: 'fetching' | 'syncing' | 'completed' | 'failed';
  progress: number;
  etaSeconds: number | null;
  error?: string;
}

interface TracksState {
  tracks: TrackSummary[];
  currentTrack: TrackDetail | null;
  renders: Record<string, RenderJobStatus>; // keyed by jobId
  syncs: Record<string, SyncStatus>; // keyed by provider
  loading: boolean;
  error: string | null;
}

const initialState: TracksState = {
  tracks: [],
  currentTrack: null,
  renders: {},
  syncs: {},
  loading: false,
  error: null,
};

const tracksSlice = createSlice({
  name: 'tracks',
  initialState,
  reducers: {
    setTracks: (state, action: PayloadAction<TrackSummary[]>) => {
      state.tracks = action.payload;
    },
    addTrack: (state, action: PayloadAction<TrackSummary>) => {
      state.tracks.unshift(action.payload);
    },
    removeTrack: (state, action: PayloadAction<string>) => {
      state.tracks = state.tracks.filter((t) => t.id !== action.payload);
      if (state.currentTrack?.id === action.payload) {
        state.currentTrack = null;
      }
    },
    setCurrentTrack: (state, action: PayloadAction<TrackDetail | null>) => {
      state.currentTrack = action.payload;
    },
    updateTrackInList: (state, action: PayloadAction<Partial<TrackSummary> & { id: string }>) => {
      state.tracks = state.tracks.map((t) => t.id === action.payload.id ? { ...t, ...action.payload } : t);
      if (state.currentTrack?.id === action.payload.id) {
        state.currentTrack = { ...state.currentTrack, ...action.payload };
      }
    },
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
    },
    updateRenderStatus: (state, action: PayloadAction<RenderJobStatus>) => {
      const job = action.payload;
      state.renders[job.jobId] = job;
    },
    removeRenderJob: (state, action: PayloadAction<string>) => {
      delete state.renders[action.payload];
    },
    setRenders: (state, action: PayloadAction<RenderJobStatus[]>) => {
      const rendersObj: Record<string, RenderJobStatus> = {};
      action.payload.forEach((job) => {
        rendersObj[job.jobId] = job;
      });
      state.renders = rendersObj;
    },
    updateSyncStatus: (state, action: PayloadAction<SyncStatus>) => {
      const sync = action.payload;
      state.syncs[sync.provider] = sync;
    },
    removeSyncStatus: (state, action: PayloadAction<string>) => {
      delete state.syncs[action.payload];
    },
    setSyncs: (state, action: PayloadAction<SyncStatus[]>) => {
      const syncsObj: Record<string, SyncStatus> = {};
      action.payload.forEach((sync) => {
        syncsObj[sync.provider] = sync;
      });
      state.syncs = syncsObj;
    },
  },
});

export const {
  setTracks,
  addTrack,
  removeTrack,
  setCurrentTrack,
  updateTrackInList,
  setLoading,
  setError,
  updateRenderStatus,
  removeRenderJob,
  setRenders,
  updateSyncStatus,
  removeSyncStatus,
  setSyncs,
} = tracksSlice.actions;

export default tracksSlice.reducer;
